/**
 * Links to, and spoken disclaimers for, a political campaign's transparency
 * notice. The notice itself is served by the ad server's transparency host
 * (`adn.ad.transparency.address` in core); `/political` renders a campaign
 * summary without needing a specific ad (see `PoliticalAdsResource` in core).
 */

// Set per environment in env/*.env: adtransparency.eu in production, the dev
// ad server in dev and the local ad server on localhost. The production ad
// server (delivery.adnuntius.com) does not serve notices, so it is no fallback.
const TRANSPARENCY_BASE = (
  import.meta.env.VITE_ADNUNTIUS_TRANSPARENCY_BASE || 'https://adtransparency.eu'
).replace(/\/+$/, '')

/** The languages the transparency notice renders in (`TransparencyNoticeRenderer.normalizeLanguage`). */
export const TRANSPARENCY_LANGUAGES = ['en', 'no', 'sv', 'da', 'de', 'fi'] as const
export type TransparencyLanguage = (typeof TRANSPARENCY_LANGUAGES)[number]

/** Each language named in itself, as a listener of that language would know it. */
export const TRANSPARENCY_LANGUAGE_NAMES: Record<TransparencyLanguage, string> = {
  en: 'English',
  no: 'Norsk',
  sv: 'Svenska',
  da: 'Dansk',
  de: 'Deutsch',
  fi: 'Suomi',
}

export function transparencyLanguageFor(uiLanguage: string | undefined): TransparencyLanguage {
  const lang = (uiLanguage ?? '').slice(0, 2).toLowerCase()
  if (lang === 'nb' || lang === 'nn') return 'no'
  if (lang === 'se') return 'sv'
  return (TRANSPARENCY_LANGUAGES as readonly string[]).includes(lang) ? (lang as TransparencyLanguage) : 'en'
}

/**
 * The public notice for one topic of a campaign. A publicId is
 * `<network hex>-<external id>`, which is exactly the `n` and `campaign` pair
 * the notice page resolves; `topic` (name or id) picks the payer and budget it
 * shows, which otherwise default to the campaign's first topic.
 */
export function campaignTransparencyUrl(
  publicId: string,
  topicId: string,
  lang?: TransparencyLanguage
): string | null {
  const separator = publicId.indexOf('-')
  if (separator <= 0 || separator === publicId.length - 1) return null
  const params = new URLSearchParams({
    n: publicId.slice(0, separator),
    campaign: publicId.slice(separator + 1),
    topic: topicId,
  })
  if (lang) params.set('lang', lang)
  return `${TRANSPARENCY_BASE}/political?${params}`
}

/**
 * The ad server's notice for one political line item. The path is the line
 * item's code — its internal id in Base49, which the API only hands out on
 * the line item's creatives (`lineItemCode`). It works for either workflow:
 * the notice reads a linked campaign when there is one, and the advertiser's
 * own details otherwise (`TransparencyNoticeHandler` in core).
 */
export function lineItemTransparencyUrl(lineItemCode: string, lang?: TransparencyLanguage): string {
  const query = lang ? `?${new URLSearchParams({ lang })}` : ''
  return `${TRANSPARENCY_BASE}/${encodeURIComponent(lineItemCode)}${query}`
}

/** The transparency host as a listener would type it — a full notice URL cannot be read aloud. */
export function spokenTransparencyHost(): string {
  try {
    return new URL(TRANSPARENCY_BASE).host
  } catch {
    return TRANSPARENCY_BASE
  }
}

interface AudioScriptDetails {
  sponsorName: string
  electionName?: string
}

// Wording follows the political video overlay and preroll templates in core
// (backend/.../transcode/political/*.hbs), so audio matches what video says.
const AUDIO_SCRIPTS: Record<
  TransparencyLanguage,
  (sponsor: string, election: string | undefined, host: string) => string
> = {
  en: (s, e, h) =>
    `Political advertisement, sponsored by ${s}${e ? `, in connection with ${e}` : ''}. ` +
    `You can find the transparency notice at ${h}.`,
  no: (s, e, h) =>
    `Politisk reklame, sponset av ${s}${e ? `, knyttet til ${e}` : ''}. ` +
    `Åpenhetserklæringen finner du på ${h}.`,
  sv: (s, e, h) =>
    `Politisk reklam, sponsrad av ${s}${e ? `, kopplad till ${e}` : ''}. ` +
    `Transparensmeddelandet finns på ${h}.`,
  da: (s, e, h) =>
    `Politisk reklame, sponsoreret af ${s}${e ? `, i forbindelse med ${e}` : ''}. ` +
    `Meddelelsen om gennemsigtighed finder du på ${h}.`,
  de: (s, e, h) =>
    `Politische Werbung, gesponsert von ${s}${e ? `, im Zusammenhang mit ${e}` : ''}. ` +
    `Den Transparenzhinweis finden Sie unter ${h}.`,
  fi: (s, e, h) =>
    `Poliittinen mainos. Rahoittaja: ${s}.${e ? ` Aihe: ${e}.` : ''} ` +
    `Avoimuusilmoitus löytyy osoitteesta ${h}.`,
}

/** What the voice in an audio ad must read out: that it is political, who pays, and where the notice is. */
export function audioDisclaimerScript(lang: TransparencyLanguage, details: AudioScriptDetails): string {
  return AUDIO_SCRIPTS[lang](details.sponsorName, details.electionName, spokenTransparencyHost())
}
